// Taxonomy management page (FRMW-001).
// Three-tab layout: Active | Draft | Audit Log
// Active: read-only collapsible tree of the current active taxonomy.
// Draft: editable tree with context-menu add/delete, plus create/activate/sync controls.
// Audit Log: version history list.

import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  taxonomyApi,
  deriveVersionStatus,
  type TaxonomyNodeInput,
  type ActivationResponse,
} from '@/lib/api/frameworkConfig'
import { useEntitlements } from '@/lib/hooks/use-entitlements'
import { queryKeys } from '@/lib/api/queryKeys'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { useToast } from '@/lib/hooks/use-toast'
import { Play, RotateCcw, Loader2, Trash2 } from 'lucide-react'

import {
  TaxonomyTree,
  DraftTreeEditor,
  AddNodeDialog,
  TaxonomyVersionList,
  buildTree,
  flattenTree,
  countNodes,
  type TreeNode,
} from '@/components/features/taxonomy'

type ConfirmAction = 'activate' | 'sync' | 'delete'

interface DeleteTarget {
  node: TreeNode
  descendantCount: number
}

export function TaxonomyPage() {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const txApi = taxonomyApi(api)
  const { data: entitlements } = useEntitlements()
  const hasConfirmationGates = entitlements?.confirmation_gates === true

  // --- Local state ---
  const [activeTab, setActiveTab] = useState('active')
  const [draftTree, setDraftTree] = useState<TreeNode[] | null>(null)
  const [draftDirty, setDraftDirty] = useState(false)

  // Add node dialog state
  const [addNodeTarget, setAddNodeTarget] = useState<TreeNode | null | undefined>(undefined)
  const addNodeOpen = addNodeTarget !== undefined

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)

  // --- Queries ---
  const activeQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.taxonomy(slug),
    queryFn: () => txApi.getActive(),
    retry: (count, error) => {
      if (error instanceof ApiRequestError && error.status === 404) return false
      return count < 3
    },
  })

  const versionsQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.taxonomyVersions(slug),
    queryFn: () => txApi.listVersions(),
  })

  const versions = versionsQuery.data ?? []
  const draftVersion = versions.find((v) => deriveVersionStatus(v) === 'draft')

  // Load draft version nodes when draft exists
  const draftQuery = useQuery({
    queryKey: [...queryKeys.frameworkConfig.taxonomyVersions(slug), draftVersion?.id],
    queryFn: () => txApi.getVersion(draftVersion!.id),
    enabled: !!draftVersion,
  })

  // Sync draft tree state when draft data loads (only when not locally edited)
  useMemo(() => {
    if (draftQuery.data && !draftDirty) {
      setDraftTree(buildTree(draftQuery.data.nodes))
    }
  }, [draftQuery.data, draftDirty])

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.taxonomy(slug) })
    queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.taxonomyVersions(slug) })
  }, [queryClient, slug])

  // --- Mutations ---

  // Create draft (clone from active)
  const createDraftMutation = useMutation({
    mutationFn: () => {
      const activeTx = activeQuery.data
      if (activeTx && activeTx.nodes.length > 0) {
        // Clone: flatten the active tree to get correctly ordered inputs with derived paths
        const tree = buildTree(activeTx.nodes)
        const flat = flattenTree(tree)
        const inputs: TaxonomyNodeInput[] = flat.map((n) => ({
          parent_path: n.parentPath,
          name: n.name,
          description: n.description,
        }))
        return txApi.createVersion(inputs)
      }
      // No active taxonomy — create with a single root node
      return txApi.createVersion([{ name: 'Risk Taxonomy', description: 'Root node' }])
    },
    onSuccess: () => {
      invalidateAll()
      setDraftDirty(false)
      toast({ title: t('taxonomy.draftCreated') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  // Save draft edits
  const saveDraftMutation = useMutation({
    mutationFn: () => {
      if (!draftVersion || !draftTree) throw new Error('No draft to save')
      const flat = flattenTree(draftTree)
      const inputs: TaxonomyNodeInput[] = flat.map((n) => ({
        parent_path: n.parentPath,
        name: n.name,
        description: n.description,
      }))
      return txApi.editVersion(draftVersion.id, inputs)
    },
    onSuccess: () => {
      invalidateAll()
      setDraftDirty(false)
      toast({ title: t('taxonomy.draftUpdated') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  // Activate draft
  const activateMutation = useMutation({
    mutationFn: (): Promise<ActivationResponse> => {
      if (!draftVersion) throw new Error('No draft to activate')
      return txApi.activate(draftVersion.id)
    },
    onSuccess: (data) => {
      setConfirmAction(null)
      if (data.status === 'pending_confirmation') {
        toast({
          title: t('taxonomy.confirmationSent'),
          description: t('taxonomy.checkEmail'),
        })
        // Do NOT clear draft state — version is still a draft until confirmed
        return
      }
      invalidateAll()
      setDraftTree(null)
      setDraftDirty(false)
      toast({ title: t('taxonomy.activated') })
    },
    onError: (err) => {
      setConfirmAction(null)
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  // Sync to active (reset draft to match active taxonomy)
  const syncToActiveMutation = useMutation({
    mutationFn: () => {
      if (!draftVersion) throw new Error('No draft to sync')
      const activeTx = activeQuery.data
      if (activeTx && activeTx.nodes.length > 0) {
        const tree = buildTree(activeTx.nodes)
        const flat = flattenTree(tree)
        const inputs: TaxonomyNodeInput[] = flat.map((n) => ({
          parent_path: n.parentPath,
          name: n.name,
          description: n.description,
        }))
        return txApi.editVersion(draftVersion.id, inputs)
      }
      return txApi.editVersion(draftVersion.id, [{ name: 'Risk Taxonomy', description: 'Root node' }])
    },
    onSuccess: () => {
      invalidateAll()
      setDraftDirty(false)
      setConfirmAction(null)
      toast({ title: 'Draft reset to active taxonomy' })
    },
    onError: (err) => {
      setConfirmAction(null)
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  // --- Tree edit handlers ---

  const handleAddChild = useCallback((parentNode: TreeNode) => {
    setAddNodeTarget(parentNode)
  }, [])

  const handleAddNodeConfirm = useCallback(
    (name: string, description: string) => {
      if (!draftTree || !addNodeTarget) return

      const newNode: TreeNode = {
        id: crypto.randomUUID(),
        parentId: addNodeTarget.id,
        path: '', // Will be recalculated on save
        depth: addNodeTarget.depth + 1,
        name,
        description: description || undefined,
        children: [],
      }

      const updated = addChildToTree(draftTree, addNodeTarget.id, newNode)
      setDraftTree(updated)
      setDraftDirty(true)
      setAddNodeTarget(undefined)
    },
    [draftTree, addNodeTarget],
  )

  const handleDeleteNode = useCallback(
    (node: TreeNode) => {
      const descendantCount = countDescendants(node)
      if (descendantCount > 0) {
        setDeleteTarget({ node, descendantCount })
        setConfirmAction('delete')
      } else {
        // No children — delete immediately
        performDelete(node)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draftTree],
  )

  const performDelete = useCallback(
    (node: TreeNode) => {
      if (!draftTree) return
      const updated = removeNodeFromTree(draftTree, node.id)
      setDraftTree(updated)
      setDraftDirty(true)
      setConfirmAction(null)
      setDeleteTarget(null)
    },
    [draftTree],
  )

  // --- Loading/error states ---

  if (activeQuery.isLoading) {
    return <TableSkeleton />
  }

  if (
    activeQuery.error &&
    !(activeQuery.error instanceof ApiRequestError && activeQuery.error.status === 404)
  ) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('taxonomy.title') })}
        technicalDetail={
          activeQuery.error instanceof ApiRequestError
            ? activeQuery.error.message
            : activeQuery.error.message
        }
        onRetry={() => activeQuery.refetch()}
      />
    )
  }

  const activeTaxonomy = activeQuery.data
  const activeTree = activeTaxonomy ? buildTree(activeTaxonomy.nodes) : []
  const activeNodeCount = activeTaxonomy?.nodes.length ?? 0
  const draftNodeCount = draftTree ? countNodes(draftTree) : 0

  return (
    <div className="max-w-4xl space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold">{t('taxonomy.title')}</h1>
        <p className="text-muted-foreground">{t('taxonomy.description')}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active">
            Active
            {activeTaxonomy && (
              <Badge variant="secondary" className="ml-2">
                v{activeTaxonomy.version_number}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="draft">
            Draft
            {draftVersion && (
              <Badge variant="secondary" className="ml-2">
                v{draftVersion.version_number}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        {/* === Active Tab === */}
        <TabsContent value="active">
          {!activeTaxonomy ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                {t('taxonomy.noActiveTaxonomy')}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('taxonomy.activeVersion', { number: activeTaxonomy.version_number })} —{' '}
                  {activeNodeCount} {t('taxonomy.nodes')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TaxonomyTree roots={activeTree} />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* === Draft Tab === */}
        <TabsContent value="draft">
          {!draftVersion ? (
            // No draft exists — show create button
            <Card>
              <CardContent className="flex flex-col items-center gap-4 py-12">
                <p className="text-muted-foreground">
                  No draft taxonomy.{' '}
                  {activeTaxonomy
                    ? 'Create a new draft from the active taxonomy to make changes.'
                    : 'Create a new draft to define your taxonomy.'}
                </p>
                <Button
                  onClick={() => createDraftMutation.mutate()}
                  disabled={createDraftMutation.isPending}
                >
                  {createDraftMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    t('taxonomy.createDraft')
                  )}
                </Button>
              </CardContent>
            </Card>
          ) : draftQuery.isLoading ? (
            <TableSkeleton />
          ) : (
            <>
              {/* Draft action bar — sync far left, activate far right */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmAction('sync')}
                    disabled={syncToActiveMutation.isPending || !activeTaxonomy}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Sync to Active
                  </Button>
                </div>

                {/* Spacer */}
                <div className="flex-1" />

                <div className="flex items-center gap-2">
                  {draftDirty && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => saveDraftMutation.mutate()}
                      disabled={saveDraftMutation.isPending}
                    >
                      {saveDraftMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Draft'
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => setConfirmAction('activate')}
                    disabled={activateMutation.isPending || draftDirty}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    {t('taxonomy.activate')}
                  </Button>
                </div>
              </div>

              {draftDirty && (
                <p className="text-xs text-amber-600">
                  You have unsaved changes. Save the draft before activating.
                </p>
              )}

              {/* Draft tree */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Draft v{draftVersion.version_number} — {draftNodeCount}{' '}
                    {t('taxonomy.nodes')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {draftTree && (
                    <DraftTreeEditor
                      roots={draftTree}
                      onChange={(roots) => {
                        setDraftTree(roots)
                        setDraftDirty(true)
                      }}
                      onAddChild={handleAddChild}
                      onDeleteNode={handleDeleteNode}
                    />
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* === Audit Log Tab === */}
        <TabsContent value="audit">
          <TaxonomyVersionList
            versions={versions}
            isLoading={versionsQuery.isLoading}
          />
        </TabsContent>
      </Tabs>

      {/* Add Node Dialog */}
      <AddNodeDialog
        open={addNodeOpen}
        parentName={addNodeTarget === null ? null : addNodeTarget?.name ?? null}
        onConfirm={handleAddNodeConfirm}
        onCancel={() => setAddNodeTarget(undefined)}
      />

      {/* Activate Confirmation */}
      <AlertDialog open={confirmAction === 'activate'} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('taxonomy.activate')}</AlertDialogTitle>
            <AlertDialogDescription>
              {hasConfirmationGates
                ? t('taxonomy.activateConfirmWithEmail')
                : t('taxonomy.activateConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => activateMutation.mutate()}
              disabled={activateMutation.isPending}
            >
              {activateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Activate
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sync to Active Confirmation */}
      <AlertDialog open={confirmAction === 'sync'} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Draft to Active</AlertDialogTitle>
            <AlertDialogDescription>
              This will discard all draft changes and reset the draft to match the current active
              taxonomy. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => syncToActiveMutation.mutate()}
              disabled={syncToActiveMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {syncToActiveMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset Draft
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Node Confirmation (only for nodes with children) */}
      <AlertDialog open={confirmAction === 'delete'} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.node.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will also delete {deleteTarget?.descendantCount} child{' '}
              {deleteTarget?.descendantCount === 1 ? 'node' : 'nodes'}. This change will be applied
              when you save the draft.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && performDelete(deleteTarget.node)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// --- Tree manipulation helpers ---

/** Add a child node to a specific parent in the tree (immutable). */
function addChildToTree(roots: TreeNode[], parentId: string, child: TreeNode): TreeNode[] {
  return roots.map((node) => {
    if (node.id === parentId) {
      return { ...node, children: [...node.children, child] }
    }
    if (node.children.length > 0) {
      return { ...node, children: addChildToTree(node.children, parentId, child) }
    }
    return node
  })
}

/** Remove a node (and all its children) from the tree (immutable). */
function removeNodeFromTree(roots: TreeNode[], nodeId: string): TreeNode[] {
  return roots
    .filter((node) => node.id !== nodeId)
    .map((node) => ({
      ...node,
      children: removeNodeFromTree(node.children, nodeId),
    }))
}

/** Count all descendants of a node. */
function countDescendants(node: TreeNode): number {
  let count = 0
  for (const child of node.children) {
    count += 1 + countDescendants(child)
  }
  return count
}


